
import {
  Action,
  Component,
  Getter,
  Prop,
  State,
  Watch,
  Vue,
} from 'nuxt-property-decorator';
import {
  ProjectPageVariation,
  SanityDocumentSchema,
  ISanityDocuments,
} from '~/types/sanity-documents';
import { IPromptToLoginPayload, TSiteConfigState } from '~/store/types';
import { IProfileState } from '~/store/profile/types';
import { IGamesState, IFetchItemsForSalePayload } from '~/store/games/types';
import ProjectHero from './ProjectHero.vue';
import ProjectTabs, { Tabs } from './ProjectTabs.vue';
import MoreFilmsCarousel from './MoreFilmsCarousel.vue';
import {
  IGetProjectPayload,
  IProjectSeriesState,
  IProjectState,
  RZR_PROJECT_SLUG,
} from '~/store/projects/types';
import {
  IGetPostersPayload,
  IGetMysteryBoxesPayload,
} from '~/store/collections/types';
import { IProject } from '~/types/project';
import WatchEpisodeDialog from '~/components/Episodes/WatchEpisodeDialog.vue';
import ProjectNftPromotion from './ProjectNftPromotion.vue';
import WatchTrailerDialog from '../Episodes/WatchTrailerDialog.vue';

@Component({
  components: {
    ProjectHero,
    ProjectTabs,
    MoreFilmsCarousel,
    WatchEpisodeDialog,
    ProjectNftPromotion,
    WatchTrailerDialog,
  },
})
export default class ProjectPage extends Vue {
  @Prop(Boolean) readonly loading!: boolean;
  @Prop(Object) readonly doc!: ISanityDocuments[SanityDocumentSchema.PROJECT];

  @Getter('siteConfig')
  readonly siteConfigState!: TSiteConfigState;

  @Getter('getProjectBySlug', { namespace: 'projects' })
  readonly getProjectBySlug!: (projectSlug: string) => IProject | undefined;

  tab = '';
  variations = ProjectPageVariation;

  @Action('getProject', { namespace: 'projects' })
  private getProject!: (payload: IGetProjectPayload) => void;

  @Action('getMysteryBoxes', { namespace: 'collections' })
  private getMysteryBoxes!: (payload: IGetMysteryBoxesPayload) => void;

  @State((profile: IProfileState) => profile.user.loggedIn, {
    namespace: 'profile',
  })
  loggedIn!: boolean;

  @Action('promptToLogin')
  protected promptToLogin!: (options?: IPromptToLoginPayload) => void;

  @Action('getUserItems', { namespace: 'inventory' })
  private getUserItems!: () => void;

  @Action('getItemsForSaleByGala', { namespace: 'games' })
  private getItemsForSaleByGala!: (payload: IFetchItemsForSalePayload) => void;

  @Action('fetchItemsForSaleByGala', { namespace: 'games' })
  private fetchItemsForSaleByGala!: (
    payload: IFetchItemsForSalePayload,
  ) => void;

  @Action('getPosters', { namespace: 'collections' })
  private getPosters!: (payload: IGetPostersPayload) => void;

  @Action('closeVideo', { namespace: 'projects' })
  private closeVideo!: () => void;

  @Action('setContinueRzrEp1AfterUA', { namespace: 'projects' })
  private setContinueRzrEp1AfterUA!: () => void;

  @Action('updateProjectSeriesWatchProgress', { namespace: 'projects' })
  private updateProjectSeriesWatchProgress!: (projectSlug: string) => void;

  @Action('clearUAFlags', { namespace: 'projects' })
  private clearUAFlags!: () => void;

  @Action('showRZREpisode1', { namespace: 'projects' })
  private showRZREpisode1!: () => void;

  @Action('showFirstEpisode', { namespace: 'projects' })
  private showFirstEpisode!: (projectSlug: string) => void;

  @Action('showProjectNftPromotion', { namespace: 'projects' })
  private showProjectNftPromotion!: (projectSlug: string) => void;

  @Action('getProjectsViews', { namespace: 'projects' })
  private getProjectsViews!: (payload: { projectSlug: string }) => void;

  @State((state: IGamesState) => state.itemsForSaleByGala, {
    namespace: 'games',
  })
  readonly itemsForSale!: IGamesState['itemsForSaleByGala'];

  @State((state: IGamesState) => state.itemsForSaleState, {
    namespace: 'games',
  })
  readonly itemsForSaleState!: IGamesState['itemsForSaleState'];

  @State((projects: IProjectState) => projects.projects, {
    namespace: 'projects',
  })
  readonly projects!: Record<string, IProject>;

  @Getter('getProjectSeriesState', { namespace: 'projects' })
  readonly getProjectSeriesState!: (
    projectSlug: string,
  ) => IProjectSeriesState | undefined;

  @State((state: IProjectState) => state.showingVideo, {
    namespace: 'projects',
  })
  readonly showingVideo!: IProjectState['showingVideo'];

  @State((state: IProjectState) => state.currentEpisode, {
    namespace: 'projects',
  })
  readonly currentEpisode!: IProjectState['currentEpisode'];

  @State((state: IProjectState) => state.nextEpisode, {
    namespace: 'projects',
  })
  readonly nextEpisode!: IProjectState['nextEpisode'];

  schemas = SanityDocumentSchema;
  hasSaleEnded = false;
  hasSaleStarted = false;
  saleEndTimeout: ReturnType<typeof setTimeout> | null = null;
  saleStartTimeout: ReturnType<typeof setTimeout> | null = null;
  pollInterval: ReturnType<typeof setInterval> | null = null;
  loadingProjectTimerElapsed = false;

  showingTrailer = false;

  async fetch() {
    if (this.loggedIn) {
      this.getUserItems();
      this.getMysteryBoxes({
        slug: this.projectSlug,
      });
      await Promise.all([this.getPosters({ slug: this.projectSlug })]);
    }

    if (this.$route.params?.slug) {
      this.getItemsForSaleByGala({
        game: process.env.gameItemProductsGame, // Gala Film in prod, Fortiude for testing
        category: this.projectSlug,
      });

      this.getProject({
        projectSlug: this.projectSlug,
        getSeries: true,
        getPosters: true,
      });
    }
  }

  mounted() {
    this.$ua.trackFirstTimeUserExperienceEvent({
      userAction: 'Entering Project Page',
    });

    if (!!this.$route.query?.tab) {
      setTimeout(() => {
        this.scrollToTabs();
      }, 1);
    }

    if (this.projectHasLoaded) {
      this.scrollToTabIfNeeded();
    } else {
      // delay showing the tabs for a couple of seconds so some dependencies
      // can load like whether the project has episodes
      // use timer in case dependency loading fails for some reason
      setTimeout(() => {
        this.loadingProjectTimerElapsed = true;
      }, 3000);
    }

    this.checkUrlParamsForVideoActions();
  }

  created() {
    this.getProjectsViews({ projectSlug: this.projectSlug });
  }

  beforeDestroy() {
    this.pollInterval && clearInterval(this.pollInterval);
    this.saleEndTimeout && clearTimeout(this.saleEndTimeout);
    this.saleStartTimeout && clearTimeout(this.saleStartTimeout);
  }

  scrollToTabIfNeeded() {
    const [hashQuery] = new URL(window.location.href).hash.split('/');
    const hash = hashQuery?.substring(1)?.toLocaleLowerCase() ?? '';
    const tabExists = Object.values(Tabs).includes(hash as Tabs);

    if (tabExists) {
      setTimeout(() => {
        this.tab = hash;
        this.scrollToTabs();
      }, 1);
    }
  }

  videoClosed() {
    this.closeVideo();
    if (this.project?.series) {
      this.clearUAFlags();
      this.updateProjectSeriesWatchProgress(this.project.slug);
    }
  }

  get projectSeriesState() {
    return this.getProjectSeriesState(this.projectSlug);
  }

  get projectSlug() {
    return this.$route.params.slug;
  }

  get project() {
    return this.getProjectBySlug(this.projectSlug);
  }

  get variation() {
    return this.$sanityLookup.getSiteConfig()?.data?.projectPageVariation;
  }

  get isSaleLive() {
    if (!this.doc) {
      return false;
    }

    if (!this.itemsForSale.length) {
      return false;
    }

    if (
      this.doc?.contentAvailability &&
      !this.doc?.contentAvailability?.isBuyable
    ) {
      return false;
    }

    if (this.hasSaleEnded) {
      return false;
    }

    if (this.hasSaleStarted) {
      return true;
    }

    const saleStartDate = this.doc?.saleStartsAt
      ? new Date(this.doc.saleStartsAt)
      : undefined;
    const saleEndDate = this.doc?.saleEndsAt
      ? new Date(this.doc.saleEndsAt)
      : undefined;
    const now = new Date();
    return (
      (!saleStartDate || now > saleStartDate) &&
      (!saleEndDate || now < saleEndDate)
    );
  }

  get projectHasLoaded() {
    return (
      this.loadingProjectTimerElapsed ||
      (this.doc !== undefined && this.project !== undefined)
    );
  }

  get canCheckUrlParamsForVideoActions() {
    if (this.project?.slug && this.project?.series !== undefined) return true;

    return false;
  }

  @Watch('canCheckUrlParamsForVideoActions')
  checkUrlParamsForVideoActions() {
    const { playvideo } = this.$route?.query;

    if (!playvideo || !this.project?.slug) return;

    if (playvideo === 'trailer') {
      if (!this.project.trailerSlug) return;

      this.showingTrailer = true;
    } else if (this.project?.slug && this.project?.series) {
      if (this.project.slug === RZR_PROJECT_SLUG) {
        this.checkUrlParamsForRZRVideoActions();
      }

      if (!this.loggedIn) {
        this.promptToLogin({ redirectAfterLoginPath: this.$route.fullPath });
        return;
      }
      this.showFirstEpisode(this.project.slug);
    }

    const route = this.$router.currentRoute;
    let query = { ...route.query };
    delete query.playvideo;
    this.$router.replace({ query }).catch((err) => {
      console.log(err);
    });
  }

  // going to get rid of this function soon, leaving for now until we fix the ua watchmode experience to be sanity driven
  checkUrlParamsForRZRVideoActions() {
    const { launchrzrepisode1, watchmode } = this.$route?.query;

    if (!launchrzrepisode1) return;

    const route = this.$router.currentRoute;
    let query = { ...route.query };

    if (watchmode === 'ua') {
      this.setContinueRzrEp1AfterUA();
      delete query.watchmode;
    } else {
      this.showRZREpisode1();
    }

    delete query.launchrzrepisode1;
    this.$router.replace({ query }).catch((err) => {
      console.log(err);
    });
  }

  @Watch('$route.query', { deep: true })
  onQueryParamsChange() {
    if (this.canCheckUrlParamsForVideoActions) {
      this.checkUrlParamsForVideoActions();
    }
  }

  @Watch('projectHasLoaded', { immediate: true })
  handleProjectHasLoadedChange(
    current: boolean | undefined,
    previous: boolean | undefined,
  ) {
    if (current === previous) {
      return;
    }
    if (current) {
      this.scrollToTabIfNeeded();
      setTimeout(() => {
        if (this.project) {
          this.showProjectNftPromotion(this.project.slug);
        }
      }, 1000);
    }
  }

  @Watch('doc.saleStartsAt', { immediate: true })
  handleSaleStartChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current === previous) {
      return;
    }

    if (!current) {
      this.hasSaleStarted = false;
      return;
    }

    const newSaleStartDate = new Date(current).getTime();
    const now = Date.now();
    this.saleStartTimeout && clearTimeout(this.saleStartTimeout);

    if (now >= newSaleStartDate) {
      this.hasSaleStarted = true;
      return;
    }

    const newTimeout = newSaleStartDate - now;

    this.hasSaleStarted = false;
    this.saleStartTimeout = setTimeout(() => {
      this.hasSaleStarted = true;
    }, newTimeout);
  }

  @Watch('doc.saleEndsAt', { immediate: true })
  handleSaleEndChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current === previous) {
      return;
    }

    if (!current) {
      this.hasSaleEnded = false;
      this.saleEndTimeout && clearTimeout(this.saleEndTimeout);
      return;
    }

    const newSaleEndDate = new Date(current).getTime();
    const now = Date.now();
    this.saleEndTimeout && clearTimeout(this.saleEndTimeout);
    if (now >= newSaleEndDate) {
      this.hasSaleEnded = true;
      return;
    }

    const newTimeout = newSaleEndDate - now;
    this.hasSaleEnded = false;
    this.saleEndTimeout = setTimeout(() => {
      this.hasSaleEnded = true;
    }, newTimeout);
  }

  @Watch('siteConfigState.data.shouldPollForSaleUpdates', { immediate: true })
  handlePollEnabledChange(
    current: boolean | undefined,
    previous: boolean | undefined,
  ) {
    if (current === previous) {
      return;
    }

    this.pollInterval && clearInterval(this.pollInterval);

    if (
      !current ||
      !this.isSaleLive ||
      !this.siteConfigState.data?.saleItemPollInterval ||
      !this.$route.params?.slug
    ) {
      return;
    }

    const interval = this.siteConfigState.data?.saleItemPollInterval * 1000;
    this.pollInterval = setInterval(() => {
      this.fetchItemsForSaleByGala({
        game: process.env.gameItemProductsGame,
        category: this.$route.params.slug,
      });
    }, interval);
  }

  @Watch('siteConfigState.data.saleItemPollInterval', { immediate: true })
  handlePollIntervalDurationChange(
    current: number | undefined,
    previous: number | undefined,
  ) {
    if (current === previous) {
      return;
    }

    this.pollInterval && clearInterval(this.pollInterval);
    if (
      !current ||
      !this.isSaleLive ||
      !this.siteConfigState?.data?.shouldPollForSaleUpdates ||
      !this.$route.params?.slug
    ) {
      return;
    }

    const newInterval = current * 1000;
    this.pollInterval = setInterval(() => {
      this.fetchItemsForSaleByGala({
        game: process.env.gameItemProductsGame,
        category: this.$route.params.slug,
      });
    }, newInterval);
  }

  @Watch('isSaleLive', { immediate: true })
  handleSaleIsLiveChange(current: boolean | undefined) {
    if (current === false && !!this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }

  @Watch('itemsForSale', { immediate: true })
  handleItemsChange(current: IGamesState['itemsForSaleByGala'] | undefined) {
    if (!current || !current.length) {
      return;
    }

    const slugs: string[] = [];
    current.forEach((item) => item.filmNftSlug && slugs.push(item.filmNftSlug));
    if (slugs.length) {
      this.$sanityLookup.fetchDocument({
        _type: this.schemas.NFT_CARD,
        slug: slugs,
      });
    }
  }

  @Watch('doc', { immediate: true })
  handleDocChange(
    current: ISanityDocuments[SanityDocumentSchema.PROJECT] | undefined,
  ) {
    if (!current) {
      return;
    }

    const docRefs = this.$sanityLookup.getDocumentRefIds(current);
    if (docRefs) {
      this.$sanityLookup.fetchReference({
        _id: Object.keys(docRefs).filter(
          (id) => !!docRefs[id] && id !== current?.uaPage?._ref,
        ),
      });
    }
  }

  goToPurchaseItems() {
    if (!this.isSaleLive) {
      return;
    }

    if (
      this.variation === this.variations.V1 &&
      this.$vuetify.breakpoint.mdAndDown
    ) {
      this.tab = Tabs.OWN;
    }

    if (this.variation === this.variations.V2) {
      this.tab = Tabs.STORE;
    }

    this.scrollToTabs();
  }

  scrollToTabs() {
    (this.$refs.projectTabs as Vue)?.$el?.scrollIntoView({
      behavior: 'smooth',
    });
  }

  @Watch('$vuetify.breakpoint.mdAndDown', { immediate: true })
  handleBpChange(current: boolean | undefined, previous: boolean | undefined) {
    if (this.variation !== this.variations.V1) {
      return;
    }

    // going from 1col to 2col the "own" tab disappears from the tabs and becomes a sidebar
    if (previous === true && current === false) {
      // if own tab is active, make it so the previous tab will show after
      // the layout transition rather than the following tab.
      if (this.tab === Tabs.OWN) {
        this.tab = Tabs.ABOUT;
      }
    }
  }

  @Watch('$route.query.tab', { immediate: true })
  handleTabQueryParamChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current !== undefined && current !== previous) {
      this.tab = current;
    }
  }
}
